import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

interface UseColumnResizeProps {
  initialColumns: { id: string; width?: number }[];
  minWidth?: number;
  onResizeEnd?: (index: number, width: number) => void;
}

/**
 * Hook to manage column resizing logic.
 *
 * How to use:
 * 1. Provide initial column definitions with optional widths.
 * 2. Use returned `columnWidths` to set column widths in your table.
 * 3. Attach `onPointerDownResize` to the resize handle elements of your columns.
 *
 * @param initialColumns Initial column definitions with optional widths
 * @param minWidth Minimum width allowed for a column
 * @param onResizeEnd Callback when resizing ends, providing column index and new width
 */
export function useColumnResize({ initialColumns, minWidth = 40, onResizeEnd }: UseColumnResizeProps) {
  const [columnWidths, setColumnWidths] = useState<number[]>(initialColumns.map((c) => c.width || 100));
  const [isResizing, setIsResizing] = useState(false);

  // We store everything needed for cleanup and math in a Ref to avoid re-renders
  const resizingRef = useRef<{
    index: number;
    startX: number;
    startWidth: number;
    currentWidth: number;
    targetElem: HTMLElement;
    pointerId: number;
  } | null>(null);

  const totalTableWidth = useMemo(() => columnWidths.reduce((acc, w) => acc + w, 0), [columnWidths]);
  // Use a fingerprint to detect changes in initialColumns because on every rerender a new prop object is created
  const columnFingerprint = JSON.stringify(initialColumns.map((c) => ({ id: c.id, width: c.width })));

  useEffect(() => {
    const nextWidths = initialColumns.map((c) => c.width || 100);
    setColumnWidths(nextWidths);
  }, [columnFingerprint]);

  // Pointer Move Handler - live updates column width during dragging
  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      const resizeHandle = resizingRef.current;
      if (!resizeHandle) return;

      const delta = e.clientX - resizeHandle.startX;
      const minAllowed = Math.min(resizeHandle.startWidth, minWidth);
      const newWidth = Math.max(minAllowed, resizeHandle.startWidth + delta);
      resizeHandle.currentWidth = newWidth; // Keep Ref in sync for the Final Up event

      setColumnWidths((prev) => {
        const next = [...prev];
        next[resizeHandle.index] = newWidth;
        return next;
      });
    },
    [minWidth],
  );

  // Pointer Up Handler - finalizes resizing
  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      const resizeHandle = resizingRef.current;
      if (!resizeHandle) return;

      // 1. Release capture on the exact element that started it
      try {
        resizeHandle.targetElem.releasePointerCapture(resizeHandle.pointerId);
      } catch (err) {
        // Handle cases where element might have unmounted
      }

      // 2. Global cleanup
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      // 3. Final Persistence
      setIsResizing(false);
      if (onResizeEnd) {
        onResizeEnd(resizeHandle.index, resizeHandle.currentWidth);
      }

      resizingRef.current = null;
    },
    [onPointerMove, onResizeEnd],
  );

  // Pointer Down Handler - initiates resizing
  const onPointerDownResize = useCallback(
    (e: React.PointerEvent, index: number) => {
      if (index < 0 || index >= columnWidths.length) return;
      e.preventDefault();
      e.stopPropagation();
      const targetElem = e.currentTarget as HTMLElement;
      const pointerId = e.pointerId;

      // Lock the mouse to this handle
      targetElem.setPointerCapture(pointerId);

      resizingRef.current = {
        index,
        startX: e.clientX,
        startWidth: columnWidths[index],
        currentWidth: columnWidths[index],
        targetElem,
        pointerId,
      };

      setIsResizing(true);

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    },
    [columnWidths, onPointerMove, onPointerUp],
  );

  // Cleanup effect: If the component unmounts mid-drag, remove listeners
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      // Safe Release of Pointer Capture
      const resizeHandle = resizingRef.current;
      if (resizeHandle && resizeHandle.targetElem) {
        try {
          // Only release if the element is still in the document
          if (document.contains(resizeHandle.targetElem)) {
            resizeHandle.targetElem.releasePointerCapture(resizeHandle.pointerId);
          }
        } catch (e) {
          // Silently fail if the browser already released it
        }
      }

      resizingRef.current = null;
    };
  }, [onPointerMove, onPointerUp]);

  return {
    columnWidths,
    totalTableWidth,
    onPointerDownResize,
    isResizing,
  };
}
